import { type Location, routes } from "@src/appV2/redesign/utils/routes";

export type SessionRole = "admin" | "worker" | "workplace";

/**
 * Discriminated by `role`: admin and worker have static homes and ignore
 * workplace flags entirely; workplace requires the four flags that govern
 * its landing page (Home Health → Schedule monthly view → Schedule →
 * Facility dashboard fallback).
 *
 * `isMobileViewport` is the screen-width breakpoint (`useIsMobile()` from
 * the redesign), NOT `Platform.isMobile` (Capacitor native check). The
 * former affects the Schedule monthly-view default; the latter only
 * gates `window.location.reload()` at navigation sites.
 */
export type GetHomeRouteForSessionParams =
  | { role: "admin" }
  | { role: "worker" }
  | {
      role: "workplace";
      isMobileViewport: boolean;
      isHomeHealthEnabled: boolean;
      isScheduleTabEnabled: boolean;
      isMonthlyViewEnabled: boolean;
    };

/**
 * Resolves the landing route for a given session shape. Single source of
 * truth for "where does this user type belong when no other URL is in
 * play" — used by `renderStartPage` and by `useSwitchUser` so that
 * context switches navigate directly to the new role's home instead of
 * bouncing through `/enter` against stale Redux state (ORB-1847).
 */
export function getHomeRouteForSession(params: GetHomeRouteForSessionParams): Location {
  if (params.role === "admin") {
    return { pathname: routes.adminAccountsAgents };
  }

  if (params.role === "worker") {
    return { pathname: routes.agentDashboard };
  }

  const { isMobileViewport, isHomeHealthEnabled, isScheduleTabEnabled, isMonthlyViewEnabled } =
    params;

  if (isHomeHealthEnabled && !isMobileViewport) {
    return { pathname: routes.homeHealthCases };
  }

  if (isScheduleTabEnabled) {
    if (!isMobileViewport && isMonthlyViewEnabled) {
      return { pathname: routes.schedule, search: "?view=month" };
    }
    return { pathname: routes.schedule };
  }

  return { pathname: routes.facilityDashboard };
}

export interface SessionRoleFlags {
  isAdmin: boolean;
  isWorker: boolean;
  isWorkplace: boolean;
}

/**
 * Resolves a `SessionRole` from the boolean flags computed in
 * `mapStateToProps` (admin, worker, workplace). Used by `renderStartPage`
 * — for post-`pseudo()` derivation from a live Redux session, see
 * `useSwitchUser` instead.
 */
export function getSessionRoleFromFlags(flags: SessionRoleFlags): SessionRole | undefined {
  const { isAdmin, isWorker, isWorkplace } = flags;

  if (isAdmin) {
    return "admin";
  }
  if (isWorker) {
    return "worker";
  }
  if (isWorkplace) {
    return "workplace";
  }
  return undefined;
}
